import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc } from 'firebase/firestore';
import { db, auth } from './firebase';
import './OutstandingAdminFeesReport.css';

function OutstandingAdminFeesReport({ onClose }) {
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterSchool, setFilterSchool] = useState('all');
  const [filterTshirtSize, setFilterTshirtSize] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('school');
  const [showActiveOnly, setShowActiveOnly] = useState(true);
  const [filtersCollapsed, setFiltersCollapsed] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    applyFiltersAndSort();
  }, [students, filterSchool, filterTshirtSize, searchTerm, sortBy, showActiveOnly]);

  // Helper: get effective admin fee (checking overrides first)
  const getEffectiveAdminFee = (student) => {
    const feeOverrides = student.feeOverrides || [];
    const adminOverride = feeOverrides.find(
      o => o.overrideType === 'admin' || o.overrideType === 'both'
    );
    return adminOverride?.adminFee ?? (student.adminFee || 0);
  };

  const loadData = async () => {
    try {
      const user = auth.currentUser;
      const franchiseeRef = doc(db, 'Franchisees', user.uid);

      // Load schools
      const schoolsQuery = query(
        collection(db, 'schools'),
        where('franchiseeRef', '==', franchiseeRef),
        where('isActive', '==', true)
      );
      const schoolsSnapshot = await getDocs(schoolsQuery);
      const schoolsList = schoolsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setSchools(schoolsList);

      // Create school lookup map
      const schoolMap = {};
      schoolsList.forEach(school => {
        schoolMap[school.id] = school.schoolName;
      });

      // Load ALL students
      const studentsQuery = query(
        collection(db, 'students'),
        where('franchiseeRef', '==', franchiseeRef)
      );

      const studentsSnapshot = await getDocs(studentsQuery);
      
      const studentsWithAdminOwing = studentsSnapshot.docs.map((docSnap) => {
        const data = docSnap.data();
        
        // Get school name from reference
        let schoolName = 'Unknown School';
        if (data.schoolRef) {
          const schoolId = data.schoolRef.id;
          schoolName = schoolMap[schoolId] || 'Unknown School';
        }

        // Calculate admin fee owing
        const effectiveAdminFee = getEffectiveAdminFee(data);
        const adminPaid = data.termPayments?.admin || 0;
        const adminOwing = Math.max(0, effectiveAdminFee - adminPaid);

        return {
          id: docSnap.id,
          studentName: data.studentName || 'Unknown',
          accountNumber: data.accountNumber || 'N/A',
          schoolName: schoolName,
          schoolRef: data.schoolRef,
          isActive: data.isActive !== false,
          tShirtSize: data.tShirtSize || 'Not Set',
          tshirtOrdered: data.tshirtOrdered || false,
          adminFee: effectiveAdminFee,
          adminPaid: adminPaid,
          adminOwing: adminOwing,
          primaryContact: data.primaryContact || {},
          startDate: data.startDate,
          class: data.class || 'N/A'
        };
      });

      // Only keep students who still owe admin fees
      const studentsOwingAdmin = studentsWithAdminOwing.filter(s => s.adminOwing > 0);

      setStudents(studentsOwingAdmin);
      setLoading(false);
    } catch (error) {
      console.error('Error loading data:', error);
      alert('Error loading outstanding admin fees report');
      setLoading(false);
    }
  };

  const applyFiltersAndSort = () => {
    let filtered = [...students];

    // Active filter
    if (showActiveOnly) {
      filtered = filtered.filter(s => s.isActive);
    }

    // School filter
    if (filterSchool !== 'all') {
      filtered = filtered.filter(s => s.schoolRef?.id === filterSchool);
    }

    // T-shirt size filter
    if (filterTshirtSize !== 'all') {
      filtered = filtered.filter(s => s.tShirtSize === filterTshirtSize);
    }

    // Search filter
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(s => {
        const name = s.studentName?.toLowerCase() || '';
        const account = s.accountNumber?.toLowerCase() || '';
        const contact = s.primaryContact?.name?.toLowerCase() || '';
        return name.includes(search) || account.includes(search) || contact.includes(search);
      });
    }

    // Sort
    switch (sortBy) {
      case 'school':
        filtered.sort((a, b) => {
          const schoolCompare = (a.schoolName || '').localeCompare(b.schoolName || '');
          if (schoolCompare !== 0) return schoolCompare;
          return (a.studentName || '').localeCompare(b.studentName || '');
        });
        break;
      case 'name-asc':
        filtered.sort((a, b) => (a.studentName || '').localeCompare(b.studentName || ''));
        break;
      case 'name-desc':
        filtered.sort((a, b) => (b.studentName || '').localeCompare(a.studentName || ''));
        break;
      case 'owing-desc':
        filtered.sort((a, b) => b.adminOwing - a.adminOwing);
        break;
      case 'owing-asc':
        filtered.sort((a, b) => a.adminOwing - b.adminOwing);
        break;
      case 'tshirt':
        filtered.sort((a, b) => {
          const sizeOrder = ['3-4yr', '5-6yr', '7-8yr', '9-10yr', '11-12yr', '13yr', 'XS', 'S', 'M', 'L', 'XL', 'XXL', 'Not Set'];
          const indexA = sizeOrder.indexOf(a.tShirtSize) >= 0 ? sizeOrder.indexOf(a.tShirtSize) : 999;
          const indexB = sizeOrder.indexOf(b.tShirtSize) >= 0 ? sizeOrder.indexOf(b.tShirtSize) : 999;
          return indexA - indexB;
        });
        break;
      default:
        break;
    }

    setFilteredStudents(filtered);
  };

  const formatCurrency = (amount) => {
    return `R${(amount || 0).toFixed(2)}`;
  };

  // Get unique T-shirt sizes from the data
  const getUniqueTshirtSizes = () => {
    const sizes = [...new Set(students.map(s => s.tShirtSize))];
    return sizes.sort();
  };

  // Calculate totals
  const getTotals = () => {
    return filteredStudents.reduce((totals, s) => ({
      adminFee: totals.adminFee + s.adminFee,
      adminPaid: totals.adminPaid + s.adminPaid,
      adminOwing: totals.adminOwing + s.adminOwing
    }), {
      adminFee: 0,
      adminPaid: 0,
      adminOwing: 0
    });
  };

  // T-shirt size summary for stock ordering
  const getTshirtSizeSummary = () => {
    const summary = {};
    filteredStudents.forEach(s => {
      const size = s.tShirtSize || 'Not Set';
      summary[size] = (summary[size] || 0) + 1;
    });
    return summary;
  };

  const exportToCSV = () => {
    const headers = [
      'School', 'Student Name', 'Account #', 'Class', 'T-Shirt Size',
      'T-Shirt Ordered', 'Admin Fee', 'Admin Paid', 'Admin Owing',
      'Status', 'Contact Name', 'Contact Phone', 'Contact Email'
    ];
    
    const rows = filteredStudents.map(student => [
      student.schoolName || 'N/A',
      student.studentName || 'N/A',
      student.accountNumber || 'N/A',
      student.class || 'N/A',
      student.tShirtSize || 'Not Set',
      student.tshirtOrdered ? 'Yes' : 'No',
      student.adminFee.toFixed(2),
      student.adminPaid.toFixed(2),
      student.adminOwing.toFixed(2),
      student.isActive ? 'Active' : 'Inactive',
      student.primaryContact?.name || 'N/A',
      student.primaryContact?.cellPhone || 'N/A',
      student.primaryContact?.email || 'N/A'
    ]);

    // Add totals row
    const totals = getTotals();
    rows.push([
      'TOTALS',
      `${filteredStudents.length} students`,
      '', '', '', '',
      totals.adminFee.toFixed(2),
      totals.adminPaid.toFixed(2),
      totals.adminOwing.toFixed(2),
      '', '', '', ''
    ]);

    // Add T-shirt size summary
    rows.push([]);
    rows.push(['T-SHIRT SIZE SUMMARY']);
    const sizeSummary = getTshirtSizeSummary();
    Object.entries(sizeSummary).forEach(([size, count]) => {
      rows.push([size, `${count} students`]);
    });

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `outstanding-admin-fees-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return <div className="admin-fees-loading">Loading outstanding admin fees report...</div>;
  }

  const totals = getTotals();
  const sizeSummary = getTshirtSizeSummary();
  const hasTshirtSizes = filteredStudents.some(s => s.tShirtSize && s.tShirtSize !== 'Not Set');

  return (
    <div className="admin-fees-container">
      <div className="admin-fees-header">
        <div className="admin-fees-header-top">
          <h1>{'\u{1F4CB}'} Outstanding Admin Fees</h1>
        </div>

        <button
          onClick={() => setFiltersCollapsed(!filtersCollapsed)}
          style={{
            background: 'linear-gradient(135deg, #e67e22 0%, #d35400)',
            color: 'white',
            border: 'none',
            padding: '12px 20px',
            borderRadius: '10px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '600',
            width: '100%',
            marginBottom: '20px',
            letterSpacing: '0.5px'
          }}
        >
          {filtersCollapsed ? '\u25BC Show Filters & Summary' : '\u25B2 Hide Filters & Summary'}
        </button>

        {!filtersCollapsed && (
          <>
            <div className="admin-fees-filters">
              <div className="admin-fees-filter-group">
                <label>School:</label>
                <select
                  value={filterSchool}
                  onChange={(e) => setFilterSchool(e.target.value)}
                  className="admin-fees-select"
                >
                  <option value="all">All Schools</option>
                  {schools.map(school => (
                    <option key={school.id} value={school.id}>
                      {school.schoolName}
                    </option>
                  ))}
                </select>
              </div>

              {hasTshirtSizes && (
                <div className="admin-fees-filter-group">
                  <label>T-Shirt Size:</label>
                  <select
                    value={filterTshirtSize}
                    onChange={(e) => setFilterTshirtSize(e.target.value)}
                    className="admin-fees-select"
                  >
                    <option value="all">All Sizes</option>
                    {getUniqueTshirtSizes().map(size => (
                      <option key={size} value={size}>{size}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="admin-fees-filter-group">
                <label>Sort By:</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="admin-fees-select"
                >
                  <option value="school">School, then Name</option>
                  <option value="name-asc">Name (A-Z)</option>
                  <option value="name-desc">Name (Z-A)</option>
                  <option value="owing-desc">Highest Owing First</option>
                  <option value="owing-asc">Lowest Owing First</option>
                  <option value="tshirt">T-Shirt Size</option>
                </select>
              </div>

              <div className="admin-fees-filter-group">
                <label>Status:</label>
                <select
                  value={showActiveOnly ? 'active' : 'all'}
                  onChange={(e) => setShowActiveOnly(e.target.value === 'active')}
                  className="admin-fees-select"
                >
                  <option value="active">Active Students Only</option>
                  <option value="all">All Students</option>
                </select>
              </div>

              <div className="admin-fees-filter-group">
                <label>Search:</label>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Name, Account #, Contact..."
                  className="admin-fees-search"
                />
              </div>

              <div className="admin-fees-filter-group">
                <label>&nbsp;</label>
                <button onClick={exportToCSV} className="admin-fees-export-btn">
                  {'\u{1F4E5}'} Export to Excel
                </button>
              </div>
            </div>

            {/* Summary Bar */}
            <div className="admin-fees-summary">
              <div className="admin-fees-summary-item">
                <span className="admin-fees-summary-label">Students Owing Admin:</span>
                <span className="admin-fees-summary-value">{filteredStudents.length}</span>
              </div>
              <div className="admin-fees-summary-item admin-fees-summary-highlight">
                <span className="admin-fees-summary-label">Total Admin Owing:</span>
                <span className="admin-fees-summary-value">{formatCurrency(totals.adminOwing)}</span>
              </div>
            </div>

            {/* T-Shirt Size Summary - only shown when students have sizes */}
            {hasTshirtSizes && Object.keys(sizeSummary).length > 0 && (
              <div className="admin-fees-tshirt-summary">
                <div className="admin-fees-tshirt-title">
                  {'\u{1F455}'} T-Shirt Sizes Needed (students still owing admin)
                </div>
                <div className="admin-fees-tshirt-chips">
                  {Object.entries(sizeSummary)
                    .sort(([a], [b]) => {
                      const sizeOrder = ['3-4yr', '5-6yr', '7-8yr', '9-10yr', '11-12yr', '13yr', 'XS', 'S', 'M', 'L', 'XL', 'XXL', 'Not Set'];
                      return (sizeOrder.indexOf(a) >= 0 ? sizeOrder.indexOf(a) : 999) - 
                             (sizeOrder.indexOf(b) >= 0 ? sizeOrder.indexOf(b) : 999);
                    })
                    .map(([size, count]) => (
                    <div key={size} className={`admin-fees-chip ${size === 'Not Set' ? 'admin-fees-chip-nosize' : ''}`}>
                      <span className="admin-fees-chip-size">{size}</span>
                      <span className="admin-fees-chip-count">{count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {filteredStudents.length === 0 ? (
        <div className="admin-fees-no-results">
          <div className="admin-fees-no-results-icon">{'\u{1F389}'}</div>
          <h2>No Outstanding Admin Fees!</h2>
          <p>All students matching your filters have paid their admin fees.</p>
        </div>
      ) : (
        <div className="admin-fees-table-wrap">
          <table className="admin-fees-table">
            <thead>
              <tr>
                <th>School</th>
                <th>Student Name</th>
                <th>Account #</th>
                {hasTshirtSizes && <th>T-Shirt Size</th>}
                <th className="admin-fees-amount-header">Admin Fee</th>
                <th className="admin-fees-amount-header">Paid</th>
                <th className="admin-fees-amount-header">Owing</th>
                <th>Contact</th>
              </tr>
            </thead>
            <tbody>
              {filteredStudents.map((student, index) => {
                // Check if this is a new school (for grouping visual)
                const isNewSchool = index === 0 || 
                  filteredStudents[index - 1].schoolName !== student.schoolName;
                
                return (
                  <tr 
                    key={student.id} 
                    className={`${!student.isActive ? 'admin-fees-inactive-row' : ''} ${isNewSchool && sortBy === 'school' ? 'admin-fees-new-school' : ''}`}
                  >
                    <td className="admin-fees-school-cell">
                      {sortBy === 'school' && !isNewSchool ? '' : student.schoolName}
                    </td>
                    <td>
                      <div className="admin-fees-name-cell">
                        {student.studentName}
                        {!student.isActive && (
                          <span className="admin-fees-inactive-badge">Inactive</span>
                        )}
                      </div>
                    </td>
                    <td className="admin-fees-account-cell">{student.accountNumber}</td>
                    {hasTshirtSizes && (
                      <td>
                        <span className={`admin-fees-size-badge ${student.tShirtSize === 'Not Set' ? 'admin-fees-size-missing' : 'admin-fees-size-set'}`}>
                          {student.tShirtSize}
                        </span>
                      </td>
                    )}
                    <td className="admin-fees-amount">
                      {formatCurrency(student.adminFee)}
                    </td>
                    <td className={`admin-fees-amount ${student.adminPaid > 0 ? 'admin-fees-paid' : 'admin-fees-zero'}`}>
                      {formatCurrency(student.adminPaid)}
                    </td>
                    <td className="admin-fees-amount admin-fees-owing">
                      {formatCurrency(student.adminOwing)}
                    </td>
                    <td>
                      <div className="admin-fees-contact">
                        <div>{student.primaryContact?.name || 'N/A'}</div>
                        <div className="admin-fees-contact-phone">
                          {student.primaryContact?.cellPhone || 'N/A'}
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="admin-fees-total-row">
                <td colSpan={hasTshirtSizes ? "4" : "3"}><strong>TOTALS ({filteredStudents.length} students):</strong></td>
                <td className="admin-fees-amount"><strong>{formatCurrency(totals.adminFee)}</strong></td>
                <td className="admin-fees-amount admin-fees-paid"><strong>{formatCurrency(totals.adminPaid)}</strong></td>
                <td className="admin-fees-amount admin-fees-owing"><strong>{formatCurrency(totals.adminOwing)}</strong></td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}

export default OutstandingAdminFeesReport;